import { Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import ExoTable from '../../components/exo/ExoTable';
import { useMemo, useState } from 'react';
import {
  ExoTableToolbar,
  ExoTableToolbarItem,
} from '../../components/exo/ExoTableToolbar';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NewBookingPage from '../../components/new/NewBookingPage';
import Header from '../../components/special/Header';
import { useTranslation } from 'react-i18next';
import { updateOrCreate } from '../../components/special/updateOrCreate';
import useFetch from '../../api/useFetch';
import ExoDialog from '../../components/exo/ExoDialog';
import { useConfig, DBRequest, API_PATCH, API_DELETE } from '../../api/api';
import { LineChart } from '@mui/x-charts';

const BookingPages = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const { apiData, isLoading, setApiData } = useFetch('booking-pages');

  const Columns = [
    {
      header: t('Name'),
      key: 'name',
      thAlign: 'left',
      tdAlign: 'left',
      sortable: true,
      width: '100%',
      cell: ({ row }) => (
        <Typography variant="body2" fontWeight={500} sx={{ color: colors.text }}>
          {row.name}
        </Typography>
      ),
    },
    {
      header: t('Duration'),
      key: 'appointmentDuration',
      thAlign: 'right',
      tdAlign: 'right',
      sortable: true,
      width: '100px',
      hiddenMobile: true,
      cell: ({ row }) => (
        <Typography
          variant="body2"
          className="monospace whitespace-nowrap"
          sx={{ color: colors.text, opacity: 0.6 }}
        >
          {row.appointmentDuration} min
        </Typography>
      ),
    },
    {
      header: t('Status'),
      key: 'isActive',
      thAlign: 'center',
      tdAlign: 'center',
      width: '100px',
      cell: ({ row }) => (
        <Box
          sx={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: row.isActive ? colors.success : colors.grey[600],
          }}
        />
      ),
    },
    {
      header: t('Link'),
      key: 'token',
      thAlign: 'center',
      tdAlign: 'center',
      width: '50px',
      hiddenMobile: true,
      cell: ({ row }) => (
        <Tooltip title={t('Copy Booking Link')}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              const url = `${window.location.origin}/book/${row.token}`;
              navigator.clipboard.writeText(url);
            }}
            sx={{ color: colors.text, opacity: 0.4, '&:hover': { opacity: 1, color: colors.primary[400] } }}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // new/edit dialog
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  function handleClose() {
    setOpen(false);
    setEditData(null);
  }

  function handleCreate(newBookingPage) {
    setApiData(updateOrCreate(apiData, newBookingPage));
    handleClose();
  }

  function handleDelete(oldBookingPage) {
    setApiData({
      ...apiData,
      data: apiData.data.filter((item) => item.id !== oldBookingPage.id),
    });
  }

  return (
    <>
      <Header title={t('Booking Pages')} />
      <ExoTableToolbar>
        <ExoTableToolbarItem
          text={t('New Booking Page')}
          icon={<PlaylistAddIcon />}
          onClick={() => {
            setEditData(null);
            setOpen(true);
          }}
        />
      </ExoTableToolbar>
      <ExoTable
        data={apiData}
        isLoading={isLoading}
        columns={Columns}
        onDelete={handleDelete}
        extendElement={({ row, onUpdate, onDelete }) => (
          <BookingPageExtend
            row={row}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onEdit={(data) => {
              setEditData(data);
              setOpen(true);
            }}
          />
        )}
      />
      <ExoDialog open={open} limitWidth>
        <NewBookingPage
          onClose={handleClose}
          onCreate={handleCreate}
          startData={editData}
        />
      </ExoDialog>
    </>
  );
};

// --- Date range helpers ---

function getDefaultRange(period) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  switch (period) {
    case 'daily': {
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'weekly': {
      const start = new Date(year, month - 2, 1);
      const end = new Date(year, month + 1, 0);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'monthly':
    case 'quarterly': {
      const start = new Date(year, 0, 1);
      const end = new Date(year, 11, 31);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'yearly': {
      const start = new Date(year - 4, 0, 1);
      const end = new Date(year, 11, 31);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    default:
      return { startDate: formatDate(new Date(year, 0, 1)), endDate: formatDate(new Date(year, 11, 31)) };
  }
}

function navigateRange(range, period, direction) {
  const start = new Date(range.startDate);
  const end = new Date(range.endDate);

  switch (period) {
    case 'daily': {
      start.setMonth(start.getMonth() + direction);
      const newEnd = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      return { startDate: formatDate(start), endDate: formatDate(newEnd) };
    }
    case 'weekly': {
      start.setMonth(start.getMonth() + direction * 3);
      end.setMonth(end.getMonth() + direction * 3);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'monthly':
    case 'quarterly': {
      start.setFullYear(start.getFullYear() + direction);
      end.setFullYear(end.getFullYear() + direction);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'yearly': {
      start.setFullYear(start.getFullYear() + direction * 5);
      end.setFullYear(end.getFullYear() + direction * 5);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    default:
      return range;
  }
}

function formatDate(date) {
  return date.toISOString().split('T')[0];
}

function formatRangeLabel(range, period) {
  const start = new Date(range.startDate);
  const end = new Date(range.endDate);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  switch (period) {
    case 'daily':
      return `${monthNames[start.getMonth()]} ${start.getFullYear()}`;
    case 'weekly':
      return `${monthNames[start.getMonth()]} ${start.getFullYear()} - ${monthNames[end.getMonth()]} ${end.getFullYear()}`;
    case 'monthly':
    case 'quarterly':
      return `${start.getFullYear()}`;
    case 'yearly':
      return `${start.getFullYear()} - ${end.getFullYear()}`;
    default:
      return `${start.getFullYear()}`;
  }
}

function generateFullTimeline(startDate, endDate, period) {
  const labels = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  switch (period) {
    case 'daily': {
      const current = new Date(start);
      while (current <= end) {
        labels.push(formatDate(current));
        current.setDate(current.getDate() + 1);
      }
      break;
    }
    case 'weekly': {
      const current = new Date(start);
      const dayOfWeek = current.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      current.setDate(current.getDate() + diff);
      while (current <= end) {
        labels.push(formatDate(current));
        current.setDate(current.getDate() + 7);
      }
      break;
    }
    case 'monthly': {
      const current = new Date(start.getFullYear(), start.getMonth(), 1);
      while (current <= end) {
        const y = current.getFullYear();
        const m = String(current.getMonth() + 1).padStart(2, '0');
        labels.push(`${y}-${m}`);
        current.setMonth(current.getMonth() + 1);
      }
      break;
    }
    case 'quarterly': {
      let year = start.getFullYear();
      let quarter = Math.ceil((start.getMonth() + 1) / 3);
      const endYear = end.getFullYear();
      const endQuarter = Math.ceil((end.getMonth() + 1) / 3);
      while (year < endYear || (year === endYear && quarter <= endQuarter)) {
        labels.push(`${year}-Q${quarter}`);
        quarter++;
        if (quarter > 4) { quarter = 1; year++; }
      }
      break;
    }
    case 'yearly': {
      for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
        labels.push(String(y));
      }
      break;
    }
    default:
      break;
  }
  return labels;
}

function formatTickLabel(label, period) {
  const monthShort = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  switch (period) {
    case 'daily': {
      const d = new Date(label);
      return String(d.getDate());
    }
    case 'weekly': {
      const d = new Date(label);
      return `${d.getDate()}.${d.getMonth() + 1}.`;
    }
    case 'monthly': {
      const m = parseInt(label.split('-')[1]) - 1;
      return monthShort[m];
    }
    case 'quarterly':
      return label.split('-')[1];
    case 'yearly':
      return label;
    default:
      return label;
  }
}

// --- BookingPageExtend component ---

const PERIODS = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'yearly', label: 'Yearly' },
];

const BookingPageExtend = ({ row, onUpdate, onDelete, onEdit }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const bookingUrl = `${window.location.origin}/book/${row.token}`;
  const shortcode = `[exopaper_booking token="${row.token}"]`;

  // Statistics state
  const [period, setPeriod] = useState('monthly');
  const [dateRange, setDateRange] = useState(() => getDefaultRange('monthly'));

  const { apiData: stats } = useFetch(
    `booking-pages/${row.id}/statistics?period=${period}&startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`
  );

  const chartData = useMemo(() => {
    const allLabels = generateFullTimeline(dateRange.startDate, dateRange.endDate, period);
    const dataMap = {};
    if (stats?.data?.timeline) {
      stats.data.timeline.forEach((item) => {
        dataMap[item.label] = item.count;
      });
    }
    return {
      xLabels: allLabels.map((label) => formatTickLabel(label, period)),
      series: allLabels.map((label) => dataMap[label] || 0),
    };
  }, [stats, dateRange, period]);

  function handlePeriodChange(newPeriod) {
    setPeriod(newPeriod);
    setDateRange(getDefaultRange(newPeriod));
  }

  function handleNavigate(direction) {
    setDateRange((prev) => navigateRange(prev, period, direction));
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 3 }}>
      {/* Info Cards */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {row.owner && (
          <Box sx={{ px: 2, py: 1.5, borderRadius: 1, bgcolor: colors.card }}>
            <Typography variant="caption" sx={{ color: colors.text, opacity: 0.5, letterSpacing: 0.5 }}>
              {t('Owner')}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text }}>
              {row.owner.firstName} {row.owner.lastName}
            </Typography>
          </Box>
        )}
        {row.targetCalendar && (
          <Box sx={{ px: 2, py: 1.5, borderRadius: 1, bgcolor: colors.card }}>
            <Typography variant="caption" sx={{ color: colors.text, opacity: 0.5, letterSpacing: 0.5 }}>
              {t('Target Calendar')}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text }}>
              {row.targetCalendar.name}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Copy Cards */}
      {[
        { label: t('Booking Link'), value: bookingUrl },
        { label: t('WordPress Shortcode'), value: shortcode },
      ].map(({ label, value }) => (
        <Box
          key={label}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            borderRadius: 1,
            bgcolor: colors.card,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: colors.text, opacity: 0.5, whiteSpace: 'nowrap', mr: 1 }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            className="monospace"
            noWrap
            sx={{ flex: 1, color: colors.text, fontSize: '0.8rem' }}
          >
            {value}
          </Typography>
          <IconButton
            size="small"
            onClick={() => navigator.clipboard.writeText(value)}
            sx={{ color: colors.text, opacity: 0.4, '&:hover': { opacity: 1, color: colors.primary[400] } }}
          >
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ))}

      {/* Statistics Card */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 2, py: 1.5, borderRadius: 1, bgcolor: colors.card }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: colors.text, opacity: 0.5 }}>
            {t('Total Bookings')}
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ color: colors.text }}>
            {stats?.data?.total ?? 0}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {PERIODS.map((p) => (
              <Typography
                key={p.key}
                variant="caption"
                onClick={() => handlePeriodChange(p.key)}
                sx={{
                  cursor: 'pointer',
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontWeight: period === p.key ? 600 : 400,
                  color: colors.text,
                  opacity: period === p.key ? 1 : 0.5,
                  bgcolor: period === p.key ? colors.selected : 'transparent',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              >
                {t(p.label)}
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => handleNavigate(-1)}
              sx={{ color: colors.text, opacity: 0.5, p: 0.25 }}
            >
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Typography
              variant="caption"
              sx={{ color: colors.text, opacity: 0.5, minWidth: 80, textAlign: 'center' }}
            >
              {formatRangeLabel(dateRange, period)}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleNavigate(1)}
              sx={{ color: colors.text, opacity: 0.5, p: 0.25 }}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ width: '100%', height: 180 }}>
          <LineChart
            xAxis={[
              {
                scaleType: 'point',
                data: chartData.xLabels,
                tickLabelStyle: {
                  fontSize: 10,
                  fill: colors.text,
                  fillOpacity: 0.4,
                },
              },
            ]}
            yAxis={[
              {
                min: 0,
                tickLabelStyle: {
                  fontSize: 10,
                  fill: colors.text,
                  fillOpacity: 0.4,
                },
              },
            ]}
            series={[
              {
                data: chartData.series,
                area: true,
                color: colors.info,
                showMark: false,
              },
            ]}
            height={180}
            grid={{ horizontal: true }}
            sx={{
              '.MuiAreaElement-root': {
                fillOpacity: 0.06,
              },
              '.MuiLineElement-root': {
                strokeWidth: 1.5,
              },
              '.MuiChartsAxis-line': {
                stroke: 'transparent',
              },
              '.MuiChartsAxis-tick': {
                stroke: 'transparent',
              },
              '.MuiChartsGrid-line': {
                stroke: colors.glass,
                strokeDasharray: '3 3',
              },
            }}
          />
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          size="small"
          startIcon={
            row.isActive
              ? <ToggleOnIcon sx={{ fontSize: 18 }} />
              : <ToggleOffIcon sx={{ fontSize: 18 }} />
          }
          onClick={() => {
            DBRequest({
              config,
              path: `booking-pages/${row.id}`,
              method: API_PATCH({ isActive: !row.isActive }),
              onResponse: (res) => onUpdate(res.data),
            });
          }}
          sx={{
            color: row.isActive ? colors.success : colors.text,
            opacity: row.isActive ? 1 : 0.5,
            textTransform: 'none',
            fontWeight: 400,
            '&:hover': {
              opacity: 1,
              bgcolor: (row.isActive ? colors.success : colors.text) + '10',
            },
          }}
        >
          {row.isActive ? t('Active') : t('Inactive')}
        </Button>
        <Button
          size="small"
          startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={() => onEdit(row)}
          sx={{
            color: colors.text,
            opacity: 0.5,
            textTransform: 'none',
            fontWeight: 400,
            '&:hover': {
              opacity: 1,
              color: colors.primary[400],
              bgcolor: colors.primary[400] + '10',
            },
          }}
        >
          {t('Edit')}
        </Button>
        <Button
          size="small"
          startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
          onClick={() => {
            DBRequest({
              config,
              path: `booking-pages/${row.id}`,
              method: API_DELETE,
              onResponse: () => onDelete(row),
            });
          }}
          sx={{
            color: colors.error,
            opacity: 0.5,
            textTransform: 'none',
            fontWeight: 400,
            '&:hover': {
              opacity: 1,
              bgcolor: colors.error + '10',
            },
          }}
        >
          {t('Delete')}
        </Button>
      </Box>
    </Box>
  );
};

export default BookingPages;
